import React, { FC, ChangeEvent, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useField, useFormikContext } from 'formik';
import { Button, Grid, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import classNames from 'classnames';

import { ContentInput } from 'api';
import { CustomSVG, DatePickers, ValidatedSwitch } from 'components';

type Event = MouseEvent<HTMLButtonElement> & ChangeEvent<HTMLInputElement>;

const useStyles = makeStyles(theme => ({
  icon: {
    marginRight: theme.spacing(1),
  },
  expiration: {
    padding: 0,
    marginRight: -theme.spacing(1) * 2,
  },
  error: {
    color: theme.palette.error.main,
  },
  active: {
    color: theme.custom.palette.black1,
  },
  inactive: {
    color: theme.custom.palette.grey2,
  },
}));

const PinPost: FC = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [field] = useField({ name: 'is_pinned' });
  const { value } = field;

  const { t } = useTranslation();
  const { values, setFieldValue, errors } = useFormikContext<ContentInput>();
  const { pinned_end_at, is_pinned } = values;
  const variant = is_pinned ? 'body2' : 'body1';

  function handleClose() {
    setAnchorEl(null);
  }

  function handleSave(time: number) {
    setFieldValue('is_pinned', true);
    setFieldValue('pinned_end_at', time);

    handleClose();
  }

  function toggleOpen(event: Event) {
    if (is_pinned) {
      setFieldValue('is_pinned', false);
      setFieldValue('pinned_end_at', null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  }

  function handleOpen(event: Event) {
    setAnchorEl(event.currentTarget);
  }

  function renderExpiration() {
    if (!pinned_end_at || !is_pinned) {
      return null;
    }

    const expiresTime = moment.unix(pinned_end_at).format('h:mm A');
    const expiresDate = moment.unix(pinned_end_at).format('MMMM D');
    const hasError = !!errors.pinned_end_at;

    return (
      <Button size="small" className={classes.expiration} onClick={handleOpen}>
        <span className={hasError ? classes.error : ''}>
          {t('content:expiresOn', { expiresTime, expiresDate })}
        </span>
      </Button>
    );
  }

  return (
    <Grid container alignItems="center" justify="space-between">
      <Grid item container xs alignItems="center">
        <CustomSVG
          name="pin"
          className={classNames(classes.icon, {
            [classes.active]: value,
            [classes.inactive]: !value,
          })}
        />
        <Tooltip title={t('posting:pinTooltip')} placement="top">
          <Typography variant={variant} className={value ? classes.active : classes.inactive}>
            {t('posting:pin')}
          </Typography>
        </Tooltip>
      </Grid>
      <Grid item container xs alignItems="center" justify="flex-end">
        {renderExpiration()}
        <ValidatedSwitch name="is_pinned" color="primary" onChange={toggleOpen} />
        <DatePickers.DateTime
          enableTime
          value={pinned_end_at}
          anchorEl={anchorEl}
          onClose={handleClose}
          onSave={handleSave}
        />
      </Grid>
    </Grid>
  );
};

export default PinPost;
